import {
  assetTypeName,
  isUpstreamPort,
  PORT_STATE_LABELS,
  PORT_STATUSES,
  PORT_TYPE_LABELS,
  subject,
  type PortCandidate,
  type PortStatus,
  type PortType,
  type PortView,
} from '@ftthnms/shared';
import { Badge, Button, type BadgeTone } from '@ftthnms/ui';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { Link2, Search, Unlink } from 'lucide-react';
import { useEffect, useRef, useState } from 'react';
import { AssetGlyph } from '../map/AssetGlyph';
import { api, metres, nf, send } from '../lib/api';
import { invalidateAssets, usePorts } from '../lib/assets';
import { useSession } from '../lib/session';
import { ConfirmDialog } from './ConfirmDialog';
import { useToast } from './Toast';

/** Administrative status (independent of whether a cable is connected). */
const STATUS_OPTION_LABELS: Record<PortStatus, string> = {
  available: 'Normal',
  reserved: 'Dipesan',
  maintenance: 'Maintenance',
};

export const PORT_STATE_TONE: Record<string, BadgeTone> = {
  available: 'neutral',
  connected: 'success',
  reserved: 'primary',
  maintenance: 'warning',
};

/** Visual port grid per port type (CON-03) with details and actions for the selected port. */
export function PortPanel({
  assetId,
  areaCode,
  onOpenAsset,
}: {
  assetId: string;
  areaCode: string | null;
  onOpenAsset: (id: string) => void;
}) {
  const ports = usePorts(assetId);
  const [selectedId, setSelectedId] = useState<string | null>(null);

  if (ports.isPending) return <span className="fn-spinner" aria-label="Memuat port" />;
  if (ports.error)
    return (
      <div className="fn-alert fn-alert--danger">
        <div className="fn-alert__body">{ports.error.message}</div>
      </div>
    );
  if (ports.data.length === 0) return <p className="fn-hint">Aset ini tidak punya port.</p>;

  const groups = (['pon', 'input', 'output'] as PortType[])
    .map((type) => ({ type, items: ports.data.filter((p) => p.type === type) }))
    .filter((g) => g.items.length > 0);
  const selected = ports.data.find((p) => p.id === selectedId) ?? null;

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 16 }}>
      {groups.map((g) => {
        const used = g.items.filter((p) => p.state === 'connected').length;
        return (
          <section key={g.type} className="fn-drawer__section">
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'baseline' }}>
              <span className="fn-panel__label" style={{ padding: 0 }}>
                {PORT_TYPE_LABELS[g.type]}
              </span>
              <span className="fn-hint num">
                {nf.format(used)}/{nf.format(g.items.length)} terpakai
              </span>
            </div>
            <div className="fn-ports" role="listbox" aria-label={PORT_TYPE_LABELS[g.type]}>
              {g.items.map((p) => (
                <button
                  key={p.id}
                  type="button"
                  role="option"
                  aria-selected={p.id === selectedId}
                  className={`fn-port${p.state === 'available' ? '' : ` fn-port--${p.state}`}`}
                  title={`${p.name} · ${PORT_STATE_LABELS[p.state]}${p.link ? ` · ${p.link.peer.customerName ?? p.link.peer.name}` : ''}`}
                  onClick={() => setSelectedId(p.id === selectedId ? null : p.id)}
                >
                  {p.no}
                </button>
              ))}
            </div>
          </section>
        );
      })}
      <div className="fn-port-legend">
        <span>
          <i /> Kosong
        </span>
        <span>
          <i className="c" /> Terpakai
        </span>
        <span>
          <i className="r" /> Dipesan
        </span>
        <span>
          <i className="m" /> Maintenance
        </span>
      </div>
      {selected ? (
        <PortDetail key={selected.id} port={selected} areaCode={areaCode} onOpenAsset={onOpenAsset} />
      ) : (
        <p className="fn-hint">Pilih port untuk melihat koneksi, menghubungkan atau mengubah statusnya.</p>
      )}
    </div>
  );
}

function PortDetail({
  port,
  areaCode,
  onOpenAsset,
}: {
  port: PortView;
  areaCode: string | null;
  onOpenAsset: (id: string) => void;
}) {
  const { ability } = useSession();
  const qc = useQueryClient();
  const toast = useToast();
  const target = subject('Asset', { areaCode });
  const canField = ability.can('field_update', target);
  const canTech = ability.can('update', target);
  const [connectOpen, setConnectOpen] = useState(false);
  const [unlinkOpen, setUnlinkOpen] = useState(false);
  const [name, setName] = useState(port.name);
  const [vlan, setVlan] = useState(port.vlanId === null ? '' : String(port.vlanId));
  const [status, setStatus] = useState<PortStatus>(port.status);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const dirty =
    name !== port.name ||
    status !== port.status ||
    (port.type === 'pon' && vlan !== (port.vlanId === null ? '' : String(port.vlanId)));

  async function save() {
    setSaving(true);
    setError(null);
    try {
      const body: Record<string, unknown> = {};
      if (status !== port.status) body.status = status;
      if (canTech && name !== port.name) body.name = name;
      if (canTech && port.type === 'pon') body.vlanId = vlan.trim() === '' ? null : Number(vlan);
      await send('PATCH', `/api/v1/ports/${port.id}`, body);
      await invalidateAssets(qc);
      toast({ tone: 'success', text: `${name} tersimpan.` });
    } catch (err) {
      setError((err as Error).message);
    } finally {
      setSaving(false);
    }
  }

  const peer = port.link?.peer;
  return (
    <section className="fn-card" style={{ background: 'var(--bg-surface-inset)' }}>
      <div className="fn-card__header">
        <div>
          <h3 className="fn-card__title">{port.name}</h3>
          <p className="fn-card__desc">
            {PORT_TYPE_LABELS[port.type]} #{port.no}
          </p>
        </div>
        <Badge tone={PORT_STATE_TONE[port.state]} dot>
          {PORT_STATE_LABELS[port.state]}
        </Badge>
      </div>
      <div className="fn-card__body" style={{ display: 'flex', flexDirection: 'column', gap: 12 }}>
        {peer ? (
          <div className="fn-choice" style={{ alignItems: 'center' }}>
            <span style={{ display: 'flex', gap: 10, alignItems: 'center', minWidth: 0 }}>
              <AssetGlyph type={peer.typeCode} />
              <span style={{ minWidth: 0 }}>
                <span className="fn-choice__title">{peer.customerName ?? peer.name}</span>
                <span className="fn-choice__desc mono">
                  {isUpstreamPort(port.type) ? '→' : '←'} {peer.code} · {peer.portName}
                  {port.link?.lengthM != null && ` · ${metres(port.link.lengthM)}`}
                </span>
              </span>
            </span>
            {!peer.restricted && (
              <Button size="sm" variant="ghost" onClick={() => onOpenAsset(peer.assetId)}>
                Buka
              </Button>
            )}
          </div>
        ) : (
          <p className="fn-hint">Port belum terhubung.</p>
        )}

        {canField && (
          <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap' }}>
            {port.link ? (
              <Button
                size="sm"
                variant="danger"
                icon={<Unlink className="i" aria-hidden />}
                onClick={() => setUnlinkOpen(true)}
              >
                Putuskan
              </Button>
            ) : (
              port.status !== 'maintenance' && (
                <Button
                  size="sm"
                  variant="primary"
                  icon={<Link2 className="i" aria-hidden />}
                  onClick={() => setConnectOpen(true)}
                >
                  Hubungkan…
                </Button>
              )
            )}
          </div>
        )}

        {(canField || canTech) && (
          <div className="fn-form-grid">
            <div className="fn-field">
              <label className="fn-label" htmlFor="port-status">
                Status port
              </label>
              <select
                id="port-status"
                className="fn-input"
                value={status}
                disabled={!canField}
                onChange={(e) => setStatus(e.target.value as PortStatus)}
              >
                {PORT_STATUSES.map((s) => (
                  <option key={s} value={s}>
                    {STATUS_OPTION_LABELS[s]}
                  </option>
                ))}
              </select>
            </div>
            <div className="fn-field">
              <label className="fn-label" htmlFor="port-name">
                Nama port
              </label>
              <input
                id="port-name"
                className="fn-input fn-input--mono"
                value={name}
                disabled={!canTech}
                onChange={(e) => setName(e.target.value)}
              />
            </div>
            {port.type === 'pon' && (
              <div className="fn-field">
                <label className="fn-label" htmlFor="port-vlan">
                  VLAN
                </label>
                <input
                  id="port-vlan"
                  className="fn-input fn-input--mono"
                  inputMode="numeric"
                  value={vlan}
                  disabled={!canTech}
                  onChange={(e) => setVlan(e.target.value)}
                  placeholder="100"
                />
              </div>
            )}
          </div>
        )}
        {error && <span className="fn-hint fn-hint--error">{error}</span>}
        {dirty && (
          <div style={{ display: 'flex', justifyContent: 'flex-end' }}>
            <Button size="sm" variant="primary" loading={saving} onClick={() => void save()}>
              Simpan Port
            </Button>
          </div>
        )}
      </div>

      {connectOpen && <ConnectDialog port={port} onClose={() => setConnectOpen(false)} />}
      {unlinkOpen && port.link && (
        <ConfirmDialog
          title={`Putuskan ${port.name}?`}
          icon={<Unlink className="i" aria-hidden />}
          confirmLabel="Putuskan"
          onClose={() => setUnlinkOpen(false)}
          onConfirm={async () => {
            await send('DELETE', `/api/v1/port-links/${port.link!.id}`);
            await invalidateAssets(qc);
            setUnlinkOpen(false);
            toast({ tone: 'success', text: `${port.name} diputus dari ${peer?.code}.` });
          }}
        >
          Koneksi ke <strong>{peer?.customerName ?? peer?.name}</strong> ({peer?.code} · {peer?.portName})
          akan dihapus. Port kedua sisi menjadi kosong.
        </ConfirmDialog>
      )}
    </section>
  );
}

/** Pick an asset nearby and one of its free ports on the opposite side (CON-05/06). */
export function ConnectDialog({ port, onClose }: { port: PortView; onClose: () => void }) {
  const ref = useRef<HTMLDialogElement>(null);
  const qc = useQueryClient();
  const toast = useToast();
  const [q, setQ] = useState('');
  const [debounced, setDebounced] = useState('');
  const [chosen, setChosen] = useState<{ candidate: PortCandidate; portId: string } | null>(null);
  const [length, setLength] = useState('');
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (ref.current && !ref.current.open) ref.current.showModal();
  }, []);
  useEffect(() => {
    const t = setTimeout(() => setDebounced(q.trim()), 250);
    return () => clearTimeout(t);
  }, [q]);

  const candidates = useQuery({
    queryKey: ['port-candidates', port.id, debounced],
    queryFn: () =>
      api<PortCandidate[]>(
        `/api/v1/ports/${port.id}/candidates${debounced ? `?q=${encodeURIComponent(debounced)}` : ''}`,
      ),
  });
  const downstream = isUpstreamPort(port.type);

  async function connect() {
    if (!chosen) return;
    setBusy(true);
    setError(null);
    try {
      const len = length.trim() === '' ? null : Number(length.replace(',', '.'));
      await send('POST', '/api/v1/port-links', {
        portId: port.id,
        peerPortId: chosen.portId,
        lengthM: Number.isFinite(len) ? len : null,
      });
      await invalidateAssets(qc);
      qc.removeQueries({ queryKey: ['port-candidates'] });
      toast({ tone: 'success', text: `${port.name} terhubung ke ${chosen.candidate.code}.` });
      ref.current?.close();
    } catch (err) {
      setError((err as Error).message);
    } finally {
      setBusy(false);
    }
  }

  return (
    <dialog
      ref={ref}
      className="fn-dialog"
      style={{ maxWidth: 520 }}
      onClose={onClose}
      aria-labelledby="connect-title"
    >
      <div
        className="fn-panel"
        style={{
          background: 'var(--bg-surface)',
          padding: 20,
          display: 'flex',
          flexDirection: 'column',
          gap: 14,
        }}
      >
        <div>
          <h2 id="connect-title" className="fn-card__title" style={{ fontSize: 15 }}>
            Hubungkan {port.name}
          </h2>
          <p className="fn-card__desc">
            {downstream
              ? 'Pilih aset di bawahnya (ODC/ODP/pelanggan) dan port input yang kosong.'
              : 'Pilih sumber sinyal (OLT/ODC/ODP) dan port keluaran yang kosong.'}{' '}
            Diurutkan dari yang terdekat.
          </p>
        </div>
        <div className="fn-input-group">
          <Search className="i" aria-hidden />
          <input
            className="fn-input"
            aria-label="Cari aset"
            placeholder="Cari nama, kode, pelanggan…"
            value={q}
            onChange={(e) => setQ(e.target.value)}
            autoFocus
          />
        </div>
        <div style={{ maxHeight: 320, overflowY: 'auto', display: 'flex', flexDirection: 'column', gap: 6 }}>
          {candidates.isPending && <span className="fn-spinner" aria-label="Memuat" />}
          {candidates.data?.length === 0 && (
            <p className="fn-hint">Tidak ada aset dengan port kosong yang cocok di wilayah Anda.</p>
          )}
          {candidates.data?.map((c) => {
            const active = chosen?.candidate.assetId === c.assetId;
            return (
              <div
                key={c.assetId}
                className="fn-choice"
                style={{
                  flexDirection: 'column',
                  alignItems: 'stretch',
                  gap: 8,
                  borderColor: active ? 'var(--primary-border)' : undefined,
                }}
              >
                <button
                  type="button"
                  style={{
                    display: 'flex',
                    gap: 10,
                    alignItems: 'center',
                    background: 'none',
                    border: 0,
                    padding: 0,
                    textAlign: 'left',
                    cursor: 'pointer',
                    color: 'inherit',
                  }}
                  onClick={() => {
                    setChosen({ candidate: c, portId: c.ports[0]!.id });
                    setLength(String(Math.round(c.distanceM * 1.15)));
                  }}
                >
                  <AssetGlyph type={c.typeCode} />
                  <span style={{ flex: 1, minWidth: 0 }}>
                    <span className="fn-choice__title">{c.customerName ?? c.name}</span>
                    <span className="fn-choice__desc mono">
                      {c.code} · {assetTypeName(c.typeCode)} · {c.ports.length} port kosong
                    </span>
                  </span>
                  <span className="fn-hint num">{metres(c.distanceM)}</span>
                </button>
                {active && c.ports.length > 1 && (
                  <div
                    style={{ display: 'flex', gap: 4, flexWrap: 'wrap' }}
                    role="radiogroup"
                    aria-label="Port tujuan"
                  >
                    {c.ports.map((p) => (
                      <button
                        key={p.id}
                        type="button"
                        role="radio"
                        aria-checked={chosen.portId === p.id}
                        className="fn-segmented__item"
                        aria-pressed={chosen.portId === p.id}
                        onClick={() => setChosen({ candidate: c, portId: p.id })}
                      >
                        {p.name}
                      </button>
                    ))}
                  </div>
                )}
              </div>
            );
          })}
        </div>
        {chosen && (
          <div className="fn-field">
            <label className="fn-label" htmlFor="link-length">
              Panjang kabel (m)
            </label>
            <input
              id="link-length"
              className="fn-input fn-input--mono"
              inputMode="decimal"
              value={length}
              onChange={(e) => setLength(e.target.value)}
            />
            <span className="fn-hint">
              Perkiraan dari jarak lurus + 15%; ganti dengan hasil ukur bila ada.
            </span>
          </div>
        )}
        {error && (
          <div className="fn-alert fn-alert--danger" role="alert">
            <div className="fn-alert__body">{error}</div>
          </div>
        )}
        <div style={{ display: 'flex', gap: 8, justifyContent: 'flex-end' }}>
          <Button variant="ghost" onClick={() => ref.current?.close()} disabled={busy}>
            Batal
          </Button>
          <Button
            variant="primary"
            disabled={!chosen}
            loading={busy}
            onClick={() => void connect()}
            icon={<Link2 className="i" aria-hidden />}
          >
            Hubungkan
          </Button>
        </div>
      </div>
    </dialog>
  );
}
